import React from "react";
import { Box, IconButton, Typography, Tooltip } from "@mui/material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { fieldTypeLabelMap } from "../../../../../static/staticdata";

const Field = ({
  field,
  fieldIndex,
  section,
  isDragging,
  draggedItemRef,
  activeFieldDropData,
  setActiveFieldDropData,
  handleFieldDragStart,
  handleDragEnd,
  handleDragOver,
  handleFieldReorderDrop,
  handleUpdateFieldLabel,
  handleFieldMenuOpen,
  getFieldWidth,
  dragTarget,
  isDefaultSection,
}) => {
  return (
    <React.Fragment>
      {isDragging &&
        draggedItemRef.current?.type === "field" &&
        activeFieldDropData.sectionId === section.id &&
        activeFieldDropData.fieldIndex === fieldIndex && (
          <Box
            onDragOver={handleDragOver}
            onDragEnter={() => {
              setActiveFieldDropData({
                sectionId: section.id,
                fieldIndex: fieldIndex,
              });
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) {
                setActiveFieldDropData({
                  sectionId: null,
                  fieldIndex: null,
                });
              }
            }}
            onDrop={(e) => handleFieldReorderDrop(e, section.id, fieldIndex)}
            sx={{
              width: getFieldWidth(section.columns),
              borderRadius: "0.5rem",
              border: "1px dashed #e0e0e0",
              backgroundColor: "rgba(76, 175, 80, 0.1)",
              height: "3rem",
            }}
          />
        )}

      <Box
        draggable
        onDragStart={(e) =>
          handleFieldDragStart(e, field, section.id, fieldIndex)
        }
        onDragEnd={handleDragEnd}
        onDragEnter={() => {
          if (isDragging && draggedItemRef.current?.type === "field") {
            setActiveFieldDropData({
              sectionId: section.id,
              fieldIndex: fieldIndex,
            });
          }
        }}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "0.5rem",
          border: "1px solid #e5e5e5",
          backgroundColor: [
            "hourly_rate",
            "flat_rate",
            "contingency_rate",
          ].includes(field.name)
            ? "tranparent"
            : "#ffffff",
          borderRadius: "0.5rem",
          padding: "0.5rem",
          boxShadow: "0px 1px 3px rgba(0,0,0,0.05)",
          transition: "all 0.2s ease",
          width: getFieldWidth(section.columns),
          cursor: isDefaultSection ? "default" : "grab",
          ...(dragTarget.type === "field" &&
          dragTarget.sectionId === section.id &&
          dragTarget.fieldIndex === fieldIndex
            ? {
                borderColor: "primary.main",
                backgroundColor: "rgba(76, 175, 80, 0.05)",
              }
            : {}),
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            flex: 1,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <input
            type="text"
            value={field.label || ""}
            onChange={(e) =>
              handleUpdateFieldLabel(section.id, field.id, e.target.value)
            }
            style={{
              border: "1px solid #e0e0e0",
              borderRadius: "0.2rem",
              padding: "0.2rem",
              fontSize: "0.8rem",
              flex: 1,
            }}
            readOnly={field?.isEditable === false}
            disabled={
              isDefaultSection ||
              ["hourly_rate", "flat_rate", "contingency_rate"].includes(
                field.name
              )
            }
          />
          {field.required && (
            <Box
              sx={{
                position: "absolute",
                top: "0rem",
                right: "0rem",
                width: "0",
                height: "0",
                borderRight: "10px solid #dc3545",
                borderBottom: "10px solid transparent",
                zIndex: 1,
              }}
            />
          )}
        </Box>
        <Typography
          variant="body1"
          sx={{
            fontSize: "0.8rem",
            whiteSpace: "nowrap",
          }}
        >
          {fieldTypeLabelMap[field?.value] || "Unknown Field Type"}
        </Typography>
        {["hourly_rate", "flat_rate", "contingency_rate"].includes(
          field.name
        ) ? (
          <Tooltip title="Visible only when the billing method corresponds to this rate type.">
            <IconButton size="small">
              <BlockRoundedIcon color="error" sx={{ fontSize: "0.8rem" }} />
            </IconButton>
          </Tooltip>
        ) : field?.isEditable === false ? (
          <Tooltip title="This field is system-defined and cannot be edited.">
            <IconButton size="small">
              <WarningAmberRoundedIcon
                color="warning"
                sx={{ fontSize: "0.8rem" }}
              />
            </IconButton>
          </Tooltip>
        ) : (
          <IconButton
            size="small"
            onClick={(e) => {
              handleFieldMenuOpen(e, section.id, field.id);
            }}
            disabled={isDefaultSection}
          >
            <SettingsOutlinedIcon sx={{ fontSize: "0.8rem" }} />
          </IconButton>
        )}
      </Box>
    </React.Fragment>
  );
};

export default Field;
